import { useEffect, useRef } from "react"
import { Box, makeStyles, useTheme } from "@material-ui/core"
import { Trans } from "@lingui/macro"
import { DOWNLOAD_STATUS } from "@data/enums"
import { useSubscriptionDownloadUpdated } from "@data/downloads/useQueryDownloads"
import { useMountEffect } from "@utils/useMountEffect"
import { toId } from "@utils/data"
import { useDownload } from "@utils/useDownload"
import { LinkButton } from "../LinkButton/LinkButton"
import useSnackbar from "../SnackbarProvider/useSnackbar"
import { Icon } from "../Icon/Icon"

const useStyles = makeStyles((theme) => ({
  iconButtonWrapper: {
    position: "relative"
  },
  circularProgress: {
    pointerEvents: "none",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 1,
    cursor: "pointer"
  },
  badge: {
    "& .MuiBadge-badge": {
      right: 2,
      top: 4,
      fontWeight: "600",
      border: `2px solid ${theme.palette.background.paper}`,
      padding: theme.spacing(0.5, 1)
    }
  },
  snackbarLink: {
    color: theme.palette.text.white,
    marginLeft: 10
  }
}))

const useDownloadReadySnackbar = () => {
  const theme = useTheme()
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { downloadFile } = useDownload()
  const { data: downloadUpdatedData } = useSubscriptionDownloadUpdated()
  const downloadQueueRef = useRef([])
  const shownIdsRef = useRef(new Set())
  const timerRef = useRef(null)

  useEffect(() => {
    if (!downloadUpdatedData?.downloadUpdated) {
      return
    }

    const download = downloadUpdatedData.downloadUpdated
    if (
      download.status !== DOWNLOAD_STATUS.COMPLETED ||
      downloadQueueRef.current.find((queuedDownload) => toId(queuedDownload) === toId(download)) ||
      shownIdsRef.current.has(toId(download))
    ) {
      return
    }

    downloadQueueRef.current.push(download)

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      const files = [...downloadQueueRef.current]
      downloadQueueRef.current = []

      if (files.length === 1) {
        const file = files[0]
        const id = toId(file)

        shownIdsRef.current.clear()
        shownIdsRef.current.add(id)

        snackbar.showMessage({
          message: (
            <Box>
              <Trans>Download ready</Trans>{" "}
              <LinkButton
                onClick={() =>
                  downloadFile({
                    ...file,
                    url: file?.path
                  })
                }
                className={classes.snackbarLink}
              >
                <Trans>Download</Trans>
              </LinkButton>
            </Box>
          ),
          icon: <Icon name="download" />
        })
      } else if (files.length > 1) {
        const count = files.length

        shownIdsRef.current.clear()
        files.forEach((file) => shownIdsRef.current.add(toId(file)))

        snackbar.showMessage({
          message: (
            <Box>
              <Box component="span">
                <strong>{count} </strong>
                <Trans>downloads completed</Trans>
              </Box>
              <LinkButton component="a" href="/downloads" className={classes.snackbarLink}>
                <Trans>View all</Trans>
              </LinkButton>
            </Box>
          ),
          icon: <Icon name="download" />
        })
      }
    }, 2000)
  }, [classes, downloadFile, downloadUpdatedData, snackbar, theme])

  useMountEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      downloadQueueRef.current = []
      if (shownIdsRef.current && typeof shownIdsRef.current.clear === "function") {
        shownIdsRef.current.clear()
      }
    }
  })
}

export { useDownloadReadySnackbar }
